import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { z } from "zod";
import { Mail, Send, CheckCircle2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Footer } from "@/components/Footer";

export const Route = createFileRoute("/contact")({
  head: () => ({
    meta: [
      { title: "Contact — LendyTrack" },
      { name: "description", content: "Send LendyTrack a message. We respond to questions, partnership inquiries, and support requests." },
      { property: "og:title", content: "Contact LendyTrack" },
      { property: "og:description", content: "Get in touch with the LendyTrack team." },
    ],
    links: [{ rel: "canonical", href: "/contact" }],
  }),
  component: ContactPage,
});

const schema = z.object({
  name: z.string().trim().min(1, "Name is required").max(100),
  email: z.string().trim().email("Enter a valid email").max(255),
  subject: z.string().trim().max(200).optional().or(z.literal("")),
  message: z.string().trim().min(10, "Message must be at least 10 characters").max(5000),
});

function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [status, setStatus] = useState<"idle" | "sending" | "sent" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState("");

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    const parsed = schema.safeParse(form);
    if (!parsed.success) {
      const fe: Record<string, string> = {};
      for (const issue of parsed.error.issues) fe[issue.path[0] as string] = issue.message;
      setErrors(fe);
      return;
    }
    setErrors({});
    setStatus("sending");
    const { error } = await supabase.from("contact_messages").insert({
      name: parsed.data.name,
      email: parsed.data.email,
      subject: parsed.data.subject || null,
      message: parsed.data.message,
    });
    if (error) {
      setStatus("error");
      setErrorMsg("Couldn't send your message. Please try again.");
      return;
    }
    setStatus("sent");
    setForm({ name: "", email: "", subject: "", message: "" });
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card">
        <div className="mx-auto max-w-7xl px-6 py-4">
          <Link to="/" className="font-display text-lg font-semibold">LendyTrack</Link>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-6 py-16">
        <div className="text-xs uppercase tracking-widest text-muted-foreground">Contact</div>
        <h1 className="mt-2 font-display text-4xl font-semibold tracking-tight">Talk to our team</h1>
        <p className="mt-3 text-muted-foreground">
          Questions, partnership ideas, or feedback — drop us a note and we'll respond within one business day.
        </p>

        <div className="mt-10 grid gap-8 md:grid-cols-[1fr_280px]">
          <form onSubmit={onSubmit} className="rounded-2xl border border-border bg-card p-6 shadow-soft space-y-4">
            {status === "sent" ? (
              <div className="rounded-xl border border-success/30 bg-success/10 p-6 text-center">
                <CheckCircle2 className="mx-auto h-10 w-10 text-success" />
                <h2 className="mt-3 font-display text-lg font-semibold">Message received</h2>
                <p className="mt-1 text-sm text-muted-foreground">Thanks for reaching out — we'll be in touch shortly.</p>
                <button type="button" onClick={() => setStatus("idle")}
                  className="mt-4 text-sm font-semibold text-primary hover:underline">
                  Send another message
                </button>
              </div>
            ) : (
              <>
                <div className="grid gap-4 md:grid-cols-2">
                  <Field label="Name" error={errors.name}>
                    <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                      maxLength={100} required
                      className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm" />
                  </Field>
                  <Field label="Email" error={errors.email}>
                    <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                      maxLength={255} required
                      className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm" />
                  </Field>
                </div>
                <Field label="Subject (optional)" error={errors.subject}>
                  <input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })}
                    maxLength={200}
                    className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm" />
                </Field>
                <Field label="Message" error={errors.message}>
                  <textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
                    rows={6} maxLength={5000} required
                    className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm" />
                </Field>
                {status === "error" && <p className="text-sm text-danger">{errorMsg}</p>}
                <button type="submit" disabled={status === "sending"}
                  className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-50">
                  <Send className="h-4 w-4" />
                  {status === "sending" ? "Sending…" : "Send message"}
                </button>
              </>
            )}
          </form>

          <aside className="space-y-4">
            <div className="rounded-2xl border border-border bg-card p-5 shadow-soft">
              <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground">
                <Mail className="h-3 w-3" /> Email
              </div>
              <a href="mailto:support@lendytrack.com" className="mt-2 block font-semibold hover:underline">
                support@lendytrack.com
              </a>
              <p className="mt-2 text-xs text-muted-foreground">
                Prefer the form — it routes straight to our inbox and is faster than email.
              </p>
            </div>
            <div className="rounded-2xl border border-border bg-card p-5 shadow-soft text-sm text-muted-foreground">
              <div className="text-xs uppercase tracking-widest">Company</div>
              <p className="mt-2 font-semibold text-foreground">Oasis Media Group LLC</p>
              <p className="mt-1">Parent company of LendyTrack.</p>
            </div>
          </aside>
        </div>
      </main>

      <Footer />
    </div>
  );
}

function Field({ label, error, children }: { label: string; error?: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <div className="mb-1 text-xs uppercase tracking-widest text-muted-foreground">{label}</div>
      {children}
      {error && <p className="mt-1 text-xs text-danger">{error}</p>}
    </label>
  );
}
